<script setup lang="ts">
/**
 * Планирование встречи — модалка 12-2 (эпик DEV-2088).
 *
 * Сессия заметок появляется сразу, а комнату Meet заводит backend/meetings:
 * приглашение с ссылкой и .ics рассылает Google Calendar, своих писем трекер
 * не шлёт (решение 3 гриля).
 *
 * Участник без рабочего ящика в домене приглашения в календаре не получит —
 * форма говорит об этом до отправки, а не после отказа сервера.
 *
 * Та же форма правит уже запланированную встречу (`mode="edit"`, DEV-2200):
 * тема, время, длительность, повтор и у серии — область. Состав здесь не
 * правят — он живёт в шапке встречи, где видно, кто уже ответил на
 * приглашение. Отдельная модалка переноса была третьим местом с датой и
 * временем, и «сделать из встречи повторяющуюся» в ней было негде.
 */
import {
  AppButton,
  AppDatePicker,
  AppModal,
  AppSelect,
  AppSwitch,
  AppTextField,
  AppTimePicker,
  AppUserPicker,
} from '@vinprop/design-system'
import { X } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { authApi } from '@/features/auth/api'
import { useAuthStore } from '@/features/auth/store'
import { useProjectsStore } from '@/features/projects/store'

import type {
  MeetingFormPerson,
  MeetingFormPreset,
  MeetingScope,
  RescheduleMeetingInput,
  ScheduleMeetingInput,
} from '../api'
import type { Person } from '@/features/auth/types'

/**
 * Домен рабочих ящиков. Тот же, что в `GOOGLE_WORKSPACE_DOMAIN` у бэкенда:
 * приглашение в Google Calendar уходит только на него, и человека без такого
 * ящика форма предупреждает заранее.
 */
const WORKSPACE_DOMAIN = '@vinprop.vn'

/** Длительность по умолчанию — полчаса: столько идёт обычный созвон команды */
const DEFAULT_MINUTES = 30

const WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']

const props = withDefaults(defineProps<{ mode?: 'create' | 'edit'; preset?: MeetingFormPreset }>(), {
  mode: 'create',
  preset: () => ({}),
})

const emit = defineEmits<{
  close: []
  submit: [input: ScheduleMeetingInput]
  save: [input: RescheduleMeetingInput]
}>()

const { t } = useI18n()
const auth = useAuthStore()
const projects = useProjectsStore()

const isEdit = computed(() => props.mode === 'edit')
const isSeries = computed(() => isEdit.value && !!props.preset.isSeries)

/** Ближайшие полчаса: планируют обычно «на скоро», а не на произвольную минуту */
const nextHalfHour = (): string => {
  const now = new Date()

  now.setMinutes(now.getMinutes() > 30 ? 60 : 30, 0, 0)

  return now.toTimeString().slice(0, 5)
}

const pad = (value: number) => String(value).padStart(2, '0')

/** Дата и время формы — местные: в поле человек видит свой пояс */
const localDate = (when: Date) =>
  `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`

/** Вид повтора, каким его выбирают в форме, из правила бэкенда */
const repeatOf = (rule: string, start: Date): 'none' | 'daily' | 'weekly' | 'custom' => {
  const body = rule.replace(/^RRULE:/i, '').toUpperCase()

  if (!body) return 'none'
  if (body === 'FREQ=DAILY') return 'daily'
  if (body === `FREQ=WEEKLY;BYDAY=${WEEKDAYS[start.getDay()]}`) return 'weekly'

  return 'custom'
}

const presetStart = props.preset.start ?? null
const presetRepeat = repeatOf(props.preset.recurrence ?? '', presetStart ?? new Date())

const title = ref(props.preset.title ?? '')
const date = ref(presetStart ? localDate(presetStart) : new Date().toISOString().slice(0, 10))
const time = ref(presetStart ? presetStart.toTimeString().slice(0, 5) : nextHalfHour())
const duration = ref(String(props.preset.durationMinutes ?? DEFAULT_MINUTES))
const projectId = ref(props.preset.projectId ?? '')
const repeat = ref<string>(presetRepeat)
const customRule = ref(presetRepeat === 'custom' ? (props.preset.recurrence ?? '') : '')
const recording = ref(props.preset.recordingEnabled ?? true)
const invitees = ref<MeetingFormPerson[]>(props.preset.participants ?? [])
const people = ref<Person[]>([])
const isDirectoryOpen = ref(true)
const picked = ref('')
const pickerKey = ref(0)
const isSaving = ref(false)

/**
 * Область правки. У одиночной встречи выбора нет, у серии он есть всегда:
 * «эта и следующие» разрезает серию, и решать это за человека нельзя — кроме
 * смены правила повтора: она по смыслу касается всех следующих созвонов.
 */
const scope = ref<MeetingScope>(props.preset.scope ?? 'MEETING_SCOPE_THIS_ONLY')

const SCOPES: MeetingScope[] = ['MEETING_SCOPE_THIS_ONLY', 'MEETING_SCOPE_THIS_AND_FOLLOWING']

const loadPeople = async () => {
  if (people.value.length || !isDirectoryOpen.value) return

  try {
    people.value = await authApi.listUsers()
  } catch {
    // Без права на справочник звать некого — пикер честно об этом говорит.
    isDirectoryOpen.value = false
  }
}

onMounted(() => {
  // Состав правится в шапке: справочник форме правки ни к чему.
  if (!isEdit.value) void loadPeople()

  if (!projects.isLoaded) {
    void projects.load()
  }
})

const withoutMailbox = computed(() => invitees.value.filter((person) => !person.hasMailbox))

const add = (userId: string) => {
  const person = people.value.find((candidate) => candidate.id === userId)

  pickerKey.value += 1
  picked.value = ''

  if (!person || person.id === auth.user?.id) return
  if (invitees.value.some((invited) => invited.id === person.id)) return

  invitees.value = [
    ...invitees.value,
    {
      id: person.id,
      name: person.name || person.email,
      hasMailbox: person.email.toLowerCase().endsWith(WORKSPACE_DOMAIN),
    },
  ]
}

const remove = (userId: string) => {
  invitees.value = invitees.value.filter((person) => person.id !== userId)
}

const durations = computed(() => {
  const known = [
    { value: '15', label: t('meetings.schedule.minutes', { count: 15 }) },
    { value: '30', label: t('meetings.schedule.minutes', { count: 30 }) },
    { value: '45', label: t('meetings.schedule.minutes', { count: 45 }) },
    { value: '60', label: t('meetings.schedule.hour') },
    { value: '90', label: t('meetings.schedule.hourHalf') },
  ]

  // Длительность из пресета может не совпасть с шагами списка: перенесённая
  // на 40 минут встреча не должна молча стать получасовой.
  return known.some((option) => option.value === duration.value)
    ? known
    : [
        ...known,
        {
          value: duration.value,
          label: t('meetings.schedule.minutes', { count: Number(duration.value) }),
        },
      ]
})

const repeats = computed(() => [
  // У серии «не повторять» недоступно: правило повтора убирают отменой серии
  // «эту и все следующие», а не правкой одного созвона.
  { value: 'none', label: t('meetings.schedule.repeat.none'), disabled: isSeries.value },
  { value: 'daily', label: t('meetings.schedule.repeat.daily') },
  { value: 'weekly', label: t('meetings.schedule.repeat.weekly') },
  { value: 'custom', label: t('meetings.schedule.repeat.custom') },
])

const projectOptions = computed(() => [
  { value: '', label: t('meetings.schedule.projectNone') },
  ...projects.items.map((project) => ({ value: project.id, label: project.name })),
])

const startsAt = computed(() => new Date(`${date.value}T${time.value}:00`))

const endsAt = computed(() => new Date(startsAt.value.getTime() + Number(duration.value) * 60_000))

/** Правило повтора. Своя строка — как ввели: RRULE проверит Google */
const recurrence = computed(() => {
  const start = startsAt.value

  switch (repeat.value) {
    case 'daily':
      return 'RRULE:FREQ=DAILY'
    case 'weekly':
      return `RRULE:FREQ=WEEKLY;BYDAY=${WEEKDAYS[start.getDay()]}`
    case 'custom':
      return customRule.value.trim()
    default:
      return ''
  }
})

/**
 * Поменялось ли правило повтора в правке. Сравнивается выбор в форме, а не
 * собранный RRULE: перенос еженедельного созвона на другой день недели
 * «только эту» — не смена правила, и резать серию из-за него нельзя.
 */
const ruleChanged = computed(
  () =>
    isEdit.value &&
    (repeat.value !== presetRepeat ||
      (repeat.value === 'custom' && customRule.value.trim() !== (props.preset.recurrence ?? ''))),
)

/** Одиночная становится серией: новое приглашение, новая ссылка на Meet */
const converts = computed(() => isEdit.value && !isSeries.value && repeat.value !== 'none')

/** Смена правила у серии касается всех следующих созвонов — область не выбирают */
const scopeLocked = computed(() => isSeries.value && ruleChanged.value)

const effectiveScope = computed<MeetingScope>(() =>
  scopeLocked.value ? 'MEETING_SCOPE_THIS_AND_FOLLOWING' : scope.value,
)

/** Пояс формы: серия без него Calendar не примет */
const timeZone = computed(() => Intl.DateTimeFormat().resolvedOptions().timeZone)

const isValid = computed(
  () =>
    title.value.trim().length > 0 &&
    !Number.isNaN(startsAt.value.getTime()) &&
    (repeat.value !== 'custom' || customRule.value.trim().length > 0),
)

/**
 * «Начать Meet сейчас» скрывается у серии: серия не начинается «сейчас», её
 * первый созвон — такая же запись в календаре, как остальные.
 */
const canStartNow = computed(() => !isEdit.value && repeat.value === 'none')

const heading = computed(() => {
  if (isEdit.value) return t('meetings.schedule.editTitle')

  return props.preset.seriesRootId
    ? t('meetings.schedule.replanTitle')
    : t('meetings.schedule.title')
})

const submit = (startNow: boolean) => {
  if (!isValid.value || isSaving.value) return

  isSaving.value = true

  emit('submit', {
    title: title.value.trim(),
    start: startsAt.value.toISOString(),
    end: endsAt.value.toISOString(),
    participantIds: invitees.value.map((person) => person.id),
    projectId: projectId.value || undefined,
    recurrence: recurrence.value || undefined,
    timeZone: recurrence.value ? timeZone.value : undefined,
    recordingEnabled: recording.value,
    startNow,
    seriesRootId: props.preset.seriesRootId,
  })
}

const save = () => {
  if (!isValid.value || isSaving.value) return

  isSaving.value = true

  // «Эту и все следующие» у серии уходит с правилом всегда: оно собирается из
  // новой даты, и перенос еженедельного созвона на другой день недели иначе
  // оставил бы серию на старом дне — при том, что форма показывает новый.
  // Совпавшее правило бэкенд отбрасывает сам, так что одно название серию
  // не пересоздаёт. В остальных случаях правило уходит только изменённым.
  const sendsRule =
    ruleChanged.value ||
    (isSeries.value && effectiveScope.value === 'MEETING_SCOPE_THIS_AND_FOLLOWING')
  const rule = sendsRule ? recurrence.value : ''

  emit('save', {
    start: startsAt.value.toISOString(),
    end: endsAt.value.toISOString(),
    scope: isSeries.value ? effectiveScope.value : undefined,
    title: title.value.trim(),
    recurrence: rule || undefined,
    timeZone: rule ? timeZone.value : undefined,
  })
}

/** Отказ сервера возвращает форму в работу: человек правит и отправляет снова */
defineExpose({ stopSaving: () => (isSaving.value = false) })
</script>

<template>
  <AppModal model-value :title="heading" size="medium" @update:model-value="emit('close')">
    <div class="mschedule">
      <!-- Продолжение серии — до заполнения, а не после: человек должен
           знать, что созвон встанет в историю серии, а не заведёт новую. -->
      <p v-if="!isEdit && preset.seriesRootId" class="mschedule__notice">
        {{ t('meetings.schedule.continueSeries', { title: preset.title || '…' }) }}
      </p>

      <AppTextField
        v-model="title"
        :label="t('meetings.schedule.topic')"
        :placeholder="t('meetings.schedule.topicPlaceholder')"
        autofocus
      />

      <div class="mschedule__row">
        <!-- Не <label>: у пикера внутри своё поле, и подпись переслала бы ему
             второй клик — панель открывалась бы и тут же закрывалась (DEV-2105). -->
        <div class="mschedule__stacked">
          <span class="mschedule__label">{{ t('meetings.schedule.date') }}</span>
          <AppDatePicker v-model="date" />
        </div>

        <div class="mschedule__stacked">
          <span class="mschedule__label">
            {{ t('meetings.schedule.time', { zone: timeZone }) }}
          </span>
          <AppTimePicker v-model="time" :step="15" />
        </div>

        <AppSelect
          v-model="duration"
          :label="t('meetings.schedule.duration')"
          :options="durations"
        />
      </div>

      <div v-if="!isEdit" class="mschedule__field">
        <span class="mschedule__label">{{ t('meetings.schedule.participants') }}</span>

        <AppUserPicker
          :key="pickerKey"
          v-model="picked"
          :users="people"
          :placeholder="t('meetings.session.invite')"
          :empty-text="t('meetings.session.nobody')"
          :unavailable="!isDirectoryOpen"
          :unavailable-text="t('meetings.session.noDirectory')"
          @click="loadPeople()"
          @update:model-value="add"
        />

        <ul v-if="invitees.length" class="mschedule__chips">
          <li v-for="person in invitees" :key="person.id" class="mschedule__chip">
            {{ person.name }}
            <span v-if="!person.hasMailbox" class="mschedule__chip-mark">
              {{ t('meetings.schedule.noMailboxMark') }}
            </span>
            <button
              type="button"
              class="mschedule__chip-remove"
              :aria-label="t('common.actions.delete')"
              @click="remove(person.id)"
            >
              <X :size="12" aria-hidden="true" />
            </button>
          </li>
        </ul>

        <p v-if="withoutMailbox.length" class="mschedule__warning">
          {{
            t('meetings.schedule.noMailbox', {
              names: withoutMailbox.map((person) => person.name).join(', '),
            })
          }}
        </p>
      </div>

      <p v-else class="mschedule__hint">{{ t('meetings.schedule.participantsInHeader') }}</p>

      <div class="mschedule__row">
        <AppSelect
          v-model="projectId"
          :label="t('meetings.schedule.project')"
          :options="projectOptions"
        />

        <AppSelect
          v-model="repeat"
          :label="t('meetings.schedule.repeatLabel')"
          :options="repeats"
        />
      </div>

      <AppTextField
        v-if="repeat === 'custom'"
        v-model="customRule"
        :label="t('meetings.schedule.customRule')"
        placeholder="RRULE:FREQ=WEEKLY;BYDAY=MO,TH"
      />

      <!-- Что случится после «Сохранить» — до нажатия: серия пересоздаётся,
           ссылка на Meet меняется, и узнать это из тоста уже поздно. -->
      <p v-if="converts" class="mschedule__notice">{{ t('meetings.schedule.convertHint') }}</p>
      <p v-else-if="scopeLocked" class="mschedule__notice">
        {{ t('meetings.schedule.ruleChangeHint') }}
      </p>
      <p v-else-if="isSeries" class="mschedule__hint">{{ t('meetings.schedule.noStopHint') }}</p>

      <fieldset v-if="isSeries" class="mschedule__scope" :disabled="scopeLocked">
        <label v-for="option in SCOPES" :key="option" class="mschedule__scope-row">
          <input
            type="radio"
            name="edit-scope"
            :value="option"
            :checked="effectiveScope === option"
            @change="scope = option"
          />
          <span>
            {{
              t(`meetings.series.scope.${option === 'MEETING_SCOPE_THIS_ONLY' ? 'one' : 'rest'}`)
            }}
            <span class="mschedule__scope-hint">
              {{
                t(
                  `meetings.series.scopeHint.${option === 'MEETING_SCOPE_THIS_ONLY' ? 'one' : 'rest'}`,
                )
              }}
            </span>
          </span>
        </label>
      </fieldset>

      <div v-if="!isEdit" class="mschedule__record">
        <AppSwitch v-model="recording" :label="t('meetings.schedule.recording')" />
        <p class="mschedule__hint">{{ t('meetings.schedule.recordingHint') }}</p>
      </div>

      <!-- Что произойдёт после «Запланировать» — до нажатия, а не тостом
           после: приглашение уходит людям, и это стоит знать заранее. -->
      <p class="mschedule__hint">{{ t('meetings.schedule.calendarHint') }}</p>
    </div>

    <template #footer>
      <AppButton variant="secondary" @click="emit('close')">
        {{ t('common.actions.cancel') }}
      </AppButton>

      <AppButton
        v-if="canStartNow"
        variant="secondary"
        :disabled="!isValid || isSaving"
        @click="submit(true)"
      >
        {{ t('meetings.schedule.startNow') }}
      </AppButton>

      <AppButton v-if="isEdit" :disabled="!isValid || isSaving" @click="save()">
        {{ t('meetings.schedule.save') }}
      </AppButton>

      <AppButton v-else :disabled="!isValid || isSaving" @click="submit(false)">
        {{ t('meetings.schedule.submit') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.mschedule {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mschedule__row {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.mschedule__stacked {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Подпись у слотов времени своя: у AppTimePicker метки нет, а поля рядом с ним
   подписаны — без неё колонка выглядит как поле без имени. */
.mschedule__label {
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.mschedule__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mschedule__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.mschedule__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 6px 3px 10px;
  border-radius: var(--radius-pill, 999px);
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
  font-size: var(--font-size-caption);
  line-height: 18px;
}

.mschedule__chip-mark {
  color: var(--color-warning-strong, var(--color-text-muted));
  font-size: var(--font-size-overline);
}

.mschedule__chip-remove {
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border: none;
  border-radius: 50%;
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.mschedule__chip-remove:hover {
  color: var(--color-text-primary);
}

.mschedule__warning {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.mschedule__record {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mschedule__hint {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

/* Предупреждение о последствиях — полосой, как в шапке встречи: подсказка
   серым текстом теряется среди подписей полей, а здесь её пропускать нельзя. */
.mschedule__notice {
  margin: 0;
  padding: 7px 10px;
  border-radius: var(--radius-md);
  background: var(--color-warning-tint, var(--color-bg-muted));
  color: var(--color-warning-strong, var(--color-text-secondary));
  font-size: var(--font-size-caption);
  line-height: 18px;
}

.mschedule__scope {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  border: none;
}

.mschedule__scope:disabled .mschedule__scope-row {
  color: var(--color-text-muted);
  cursor: default;
}

.mschedule__scope-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  line-height: 20px;
  cursor: pointer;
}

.mschedule__scope-hint {
  display: block;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

/* На телефоне поля идут в столбик: три колонки в 390 не помещаются. */
@media (max-width: 640px) {
  .mschedule__row {
    grid-template-columns: 1fr;
  }
}
</style>
